import {
  Button,
  Divider,
  Paper,
  Stack,
  Switch,
  Typography,
} from '@mui/material';
import React, { useState } from 'react';
import { usePage } from '@inertiajs/react';
import axios from 'axios';

import {
  TaskNotificationSettings,
  User,
  UserActivitySettings,
  UserSettingsCategory,
} from '@/types';

type Props = {
  onSettingChange: () => void;
};

const SETTINGS_VALUES = {
  false: '0',
  true: '1',
};

const ActivitySettings = ({ onSettingChange }: Props): React.ReactElement => {
  const {
    props: { auth },
  } = usePage<{
    auth: { user: User };
  }>();

  const user = auth.user;

  const [settings, setSettings] = useState(
    user.settings[UserSettingsCategory.user_activities] ?? {},
  );

  const [taskNotificationSettings, setTaskNotificationSettings] = useState(
    user.settings?.[UserSettingsCategory.task_notifications] ?? {},
  );

  const handleTeamSwitchChange = async (
    event: React.ChangeEvent<HTMLInputElement>,
  ): Promise<void> => {
    const key = `${UserActivitySettings.team_activities}_${event.target.value}`;
    const newValue = event.target.checked
      ? SETTINGS_VALUES.true
      : SETTINGS_VALUES.false;

    setSettings((prevSettings) => ({
      ...prevSettings,
      [key]: newValue,
    }));

    await axios.put(route('users.settings', user), {
      category: UserSettingsCategory.user_activities,
      key,
      value: newValue,
    });

    onSettingChange();
  };

  const handleEmailSwitchChange = (
    event: React.ChangeEvent<HTMLInputElement>,
  ): void => {
    axios.put(route('users.settings', user), {
      category: UserSettingsCategory.user_activities,
      key: UserActivitySettings.daily_summary_email,
      value: event.target.checked
        ? SETTINGS_VALUES.true
        : SETTINGS_VALUES.false,
    });

    setSettings({
      ...settings,
      [UserActivitySettings.daily_summary_email]: event.target.checked
        ? SETTINGS_VALUES.true
        : SETTINGS_VALUES.false,
    });
  };

  const handleTaskAssignedChange = (
    event: React.ChangeEvent<HTMLInputElement>,
  ): void => {
    const newValue = event.target.checked
      ? SETTINGS_VALUES.true
      : SETTINGS_VALUES.false;

    setTaskNotificationSettings({
      ...taskNotificationSettings,
      [TaskNotificationSettings.task_assigned]: newValue,
    });

    axios.put(route('users.settings', user), {
      category: UserSettingsCategory.task_notifications,
      key: TaskNotificationSettings.task_assigned,
      value: newValue,
    });
  };

  const handleToggleAllTeams = async (): Promise<void> => {
    const allTeamsOff = user.teams.every(
      (team) =>
        settings[`${UserActivitySettings.team_activities}_${team.pid}`] === '0',
    );

    const updatedSettings = { ...settings };

    user.teams.forEach((team) => {
      const key = `${UserActivitySettings.team_activities}_${team.pid}`;
      updatedSettings[key] = allTeamsOff ? '1' : '0';
    });

    setSettings(updatedSettings);

    for (const team of user.teams) {
      const key = `${UserActivitySettings.team_activities}_${team.pid}`;

      await axios.put(route('users.settings', user), {
        category: UserSettingsCategory.user_activities,
        key,
        value: allTeamsOff ? SETTINGS_VALUES.true : SETTINGS_VALUES.false,
      });
    }

    onSettingChange();
  };

  return (
    <Stack
      sx={{
        width: '100%',
        px: 2,
        pb: 2,
        maxWidth: {
          lg: 600,
        },
      }}
    >
      <Stack
        sx={{
          flexDirection: 'column',
          gap: 3,
          width: '100%',
        }}
      >
        <Paper variant="outlined" sx={{ p: 2, width: '100%' }}>
          <Stack
            sx={{
              flexDirection: 'row',
              alignItems: 'center',
              gap: 1,
            }}
          >
            <Typography
              variant="body1"
              sx={{
                fontWeight: 600,
              }}
            >
              Which teams do you want to be notified about?
            </Typography>
            <Button
              variant="text"
              color="primary"
              sx={{ fontSize: 14 }}
              onClick={handleToggleAllTeams}
            >
              {user.teams.every(
                (team) =>
                  settings[
                    `${UserActivitySettings.team_activities}_${team.pid}`
                  ] === '0',
              )
                ? 'TURN ON ALL TEAMS'
                : 'TURN OFF ALL TEAMS'}
            </Button>
          </Stack>
          <Divider sx={{ my: 2 }} />
          {user.teams.map((team) => (
            <Stack
              key={team.pid}
              sx={{
                flexDirection: 'row',
                alignItems: 'center',
                gap: 1,
              }}
            >
              <Switch
                color="primary"
                value={team.pid}
                checked={
                  settings[
                    `${UserActivitySettings.team_activities}_${team.pid}`
                  ]
                    ? settings[
                        `${UserActivitySettings.team_activities}_${team.pid}`
                      ] === '1'
                    : true
                }
                onChange={handleTeamSwitchChange}
              />
              <Typography>{team.name}</Typography>
            </Stack>
          ))}
        </Paper>

        <Paper variant="outlined" sx={{ p: 2, width: '100%' }}>
          <Stack
            sx={{
              flexDirection: 'row',
              justifyContent: 'space-between',
              alignItems: 'center',
            }}
          >
            <Typography
              variant="body1"
              sx={{
                fontWeight: 600,
              }}
            >
              Do you want a daily email that summarizes those teams' client
              activity?
            </Typography>
          </Stack>
          <Divider sx={{ my: 2 }} />
          <Stack
            sx={{
              flexDirection: 'row',
              alignItems: 'center',
              gap: 1,
            }}
          >
            <Switch
              checked={
                settings['daily_summary_email']
                  ? settings['daily_summary_email'] === '1'
                  : true
              }
              color="primary"
              onChange={handleEmailSwitchChange}
            />
            <Typography>Daily summary email (sent at 8am EST)</Typography>
          </Stack>
        </Paper>

        <Paper variant="outlined" sx={{ p: 2, width: '100%' }}>
          <Stack
            sx={{
              flexDirection: 'row',
              justifyContent: 'space-between',
              alignItems: 'center',
            }}
          >
            <Typography
              variant="body1"
              sx={{
                fontWeight: 600,
              }}
            >
              Task notifications
            </Typography>
          </Stack>
          <Divider sx={{ my: 2 }} />
          <Stack
            sx={{
              flexDirection: 'row',
              alignItems: 'center',
              gap: 1,
            }}
          >
            <Switch
              checked={
                taskNotificationSettings[TaskNotificationSettings.task_assigned]
                  ? taskNotificationSettings[
                      TaskNotificationSettings.task_assigned
                    ] === '1'
                  : true
              }
              color="primary"
              onChange={handleTaskAssignedChange}
            />
            <Typography>Task assignment emails</Typography>
          </Stack>
        </Paper>
      </Stack>
    </Stack>
  );
};

export default ActivitySettings;
